<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { getAllotmentNextCertificateNo } from '@/features/startTask/api/officerAppointmentApi';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';
import {
  resolveIdTypeDocumentLabel as resolveIdTypeDocumentLabelFromOptions,
  resolveIdTypeEfilingLabel as resolveIdTypeEfilingLabelFromOptions,
} from './composables/idTypeLabelResolve';
import {
  applySerializedShareholderAddressFields,
  formatShareholderAddressLine,
  hasShareholderAddressSlotData,
  isSgForeignAddressType,
  normalizeShareholderAddressSlot,
  refreshShareholderAddressDisplay,
  serializeShareholderAddressFields,
  SG_ADDR_LOCAL,
  syncShareholderPrimaryAddressLine,
  type ShareholderAddressSlot,
} from './composables/shareholderAddressFields';
import {
  computeIssuedFromBaseline,
  fetchEntitySharesList,
  formatShareCapitalAmount,
  parseShareCapitalAmount,
  sumEntityIssuedBaseline,
} from './composables/shareCapitalIssuedBaseline';
import { mergeAllotmentShareholderListByCurrency } from './composables/mergeAllotmentByCurrency';
import SgShareholderAddressBlock from './SgShareholderAddressBlock.vue';

interface Props {
  entityId?: string;
  initialData?: any;
  readOnly?: boolean;
  validationErrors?: Record<string, string>;
  documents?: Array<{ templateName?: string; docCode?: string }>;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
}

interface ShareClassRow {
  id: string;
  classOfShares: string;
  classOfSharesName: string;
  numberOfShares: string;
  entityIssuedBaseline: string;
  issuedShareCapital: string;
  paidUpShareCapital: string;
  issuedManuallyEdited: boolean;
  subClasses: Array<{ id: string; name: string; numberOfShares: string }>;
  treasuryShares: Array<any>;
}

interface CurrencyRow {
  id: string;
  currency: string;
  currencyName: string;
  sharesPayable: string;
  shareClasses: ShareClassRow[];
}

interface ShareholderRow {
  id: string;
  shareholder: string;
  shareholderName: string;
  categoryCd: string;
  idTypeCd: string;
  idTypeName: string;
  nationalityCd: string;
  nationalityName: string;
  countryCd: string;
  dateOfBirth: string;
  mobileNo: string;
  contactEmail: string;
  contactRegNo: string;
  addressType: string;
  postalCode: string;
  blockNo: string;
  streetName: string;
  buildingName: string;
  levelNo: string;
  unitNo: string;
  foreignAddr1: string;
  foreignAddr2: string;
  alternateAddressCd: string;
  altPostalCode: string;
  altBlockNo: string;
  altStreetName: string;
  altBuildingName: string;
  altAddrLevel: string;
  altAddrUnit: string;
  altForeignAddr1: string;
  altForeignAddr2: string;
  primaryAddress: string;
  addressDisplayLine: string;
  activeAddressSlot: ShareholderAddressSlot;
  representativeCd: string;
  representativeName: string;
  currencies: CurrencyRow[];
}

const props = withDefaults(defineProps<Props>(), {
  entityId: '',
  initialData: null,
  readOnly: false,
  validationErrors: () => ({}),
  documents: () => [],
  selectedPerson: null,
  activePersonContext: null,
});
const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, any>): void;
  (e: 'validity-change', valid: boolean): void;
}>();
const readOnly = computed(() => !!props.readOnly);

const sessionStore = useSessionStore();
const entityCd = ref('');
const entityName = ref('');
const entityRegNo = ref('');
const companyAddress = ref('');
const transactionDate = ref('');
const priorApprovalFiled = ref('');
const certificateType = ref('');
const certificateNo = ref('');
const chairPerson = ref('');

const currencyOptions = ref<Array<{ title: string; value: string; modalTitle?: string; bigo3?: string }>>([]);
const classOfSharesOptions = ref<Array<{ title: string; value: string }>>([]);
const certificateTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const identificationTypeOptions = ref<Array<{ title: string; value: string; bigo1?: string }>>([]);
const addressTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const sharePayableOptions = [
  { title: 'All in cash', value: 'all-in-cash' },
  { title: 'All otherwise than in cash', value: 'all-otherwise' },
  { title: 'No consideration', value: 'no-consideration' },
  { title: 'Partially in cash and otherwise than in cash', value: 'partially' },
];
const entityTypeName = ref('');
const companyTypeName = ref('');

const MAX_DOC_CHIPS = 4;
const formRoot = ref<HTMLElement | null>(null);
const requiredErrorKeys = ref<Set<string>>(new Set());
const shareholderRows = ref<ShareholderRow[]>([]);
const addressDialogOpen = ref(false);
const addressDialogRow = ref<ShareholderRow | null>(null);
const addressDialogSlot = ref<ShareholderAddressSlot>('residential');
const dismissedSuggestions = ref<Set<string>>(new Set());
let entitySharesCacheCd = '';
let entitySharesCacheList: Array<Record<string, unknown>> = [];
const suggestedToday = computed(() => getCurrentDateByJurisdiction());
const formatDateByUserFormat = useFormatDateByUserFormat();

function normalizeIso(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(
    get,
    (v) => {
      const s = normalizeIso(v);
      if (field.dateValue.value !== s) field.dateValue.value = s;
    },
    { immediate: true },
  );
  watch(field.dateValue, (v) => {
    const s = normalizeIso(v);
    if (normalizeIso(get()) !== s) set(s);
  });
}

const transactionDateField = useDateField();
bindModelDate(
  () => transactionDate.value,
  (v) => { transactionDate.value = v; },
  transactionDateField,
);

const transactionDateMenu = transactionDateField.dateMenu;
const transactionDateLocalText = transactionDateField.localText;
const transactionDateOnBlur = transactionDateField.onDateTextBlur;
const transactionDatePickerValue = transactionDateField.datePickerValue;
const transactionDatePlaceholder = transactionDateField.datePlaceholder;
const transactionDatePickerProps = transactionDateField.datePickerProps;

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}
function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function createShareClassRow(): ShareClassRow {
  return {
    id: uid('cls'),
    classOfShares: '',
    classOfSharesName: '',
    numberOfShares: '',
    entityIssuedBaseline: '',
    issuedShareCapital: '',
    paidUpShareCapital: '',
    issuedManuallyEdited: false,
    subClasses: [],
    treasuryShares: [],
  };
}
function createCurrencyRow(): CurrencyRow {
  return {
    id: uid('cur'),
    currency: '',
    currencyName: '',
    sharesPayable: 'all-in-cash',
    shareClasses: [createShareClassRow()],
  };
}
function createShareholderRow(): ShareholderRow {
  return {
    id: uid('sh'),
    shareholder: '',
    shareholderName: '',
    categoryCd: '',
    idTypeCd: '',
    idTypeName: '',
    nationalityCd: '',
    nationalityName: '',
    countryCd: '',
    dateOfBirth: '',
    mobileNo: '',
    contactEmail: '',
    contactRegNo: '',
    addressType: SG_ADDR_LOCAL,
    postalCode: '',
    blockNo: '',
    streetName: '',
    buildingName: '',
    levelNo: '',
    unitNo: '',
    foreignAddr1: '',
    foreignAddr2: '',
    alternateAddressCd: SG_ADDR_LOCAL,
    altPostalCode: '',
    altBlockNo: '',
    altStreetName: '',
    altBuildingName: '',
    altAddrLevel: '',
    altAddrUnit: '',
    altForeignAddr1: '',
    altForeignAddr2: '',
    primaryAddress: '',
    addressDisplayLine: '',
    activeAddressSlot: 'residential',
    representativeCd: '',
    representativeName: '',
    currencies: [createCurrencyRow()],
  };
}

function getLabelByCode(code: string, list: Array<{ title: string; value: string }>): string {
  return list.find((x) => x.value === code)?.title || code;
}

function looksLikeBaseCode(value: string): boolean {
  const v = trim(value);
  return !!v && /^[A-Z]\d{5}$/i.test(v);
}

function idTypeOptionsSnapshot() {
  return identificationTypeOptions.value.map((o) => ({
    title: o.title,
    value: o.value,
    bigo1: o.bigo1,
  }));
}

function resolveIdTypeDocumentLabel(code: string): string {
  return resolveIdTypeDocumentLabelFromOptions(code, idTypeOptionsSnapshot());
}

function resolveIdTypeEfilingLabel(code: string): string {
  return resolveIdTypeEfilingLabelFromOptions(code, idTypeOptionsSnapshot());
}

function buildAddressFromEntity(ent: Record<string, any>): string {
  const postalCode = String(ent.POSTAL_CODE ?? ent.postalCode ?? '').trim();
  const blockNo = String(ent.BLOCK_NO ?? ent.blockNo ?? ent.blockHouseNo ?? '').trim();
  const streetName = String(ent.STREET_NAME ?? ent.streetName ?? '').trim();
  const addrLevel = String(ent.ADDR_LEVEL ?? ent.addrLevel ?? ent.level ?? '').trim();
  const addrUnit = String(ent.ADDR_UNIT ?? ent.addrUnit ?? ent.unit ?? '').trim();
  const buildingName = String(ent.BUILDING_NAME ?? ent.buildingName ?? ent.buildingEstateName ?? '').trim();
  const entityPostalCode = String(ent.entityPostalCode ?? '').trim();
  const entityBlockNo = String(ent.entityBlockNo ?? '').trim();
  const entityStreetName = String(ent.entityStreetName ?? '').trim();
  const entityAddrLevel = String(ent.entityAddrLevel ?? '').trim();
  const entityAddrUnit = String(ent.entityAddrUnit ?? '').trim();
  const entityBuildingName = String(ent.entityBuildingName ?? '').trim();

  const finalBlockNo = blockNo || entityBlockNo;
  const finalStreetName = streetName || entityStreetName;
  const finalAddrLevel = addrLevel || entityAddrLevel;
  const finalAddrUnit = addrUnit || entityAddrUnit;
  const finalBuildingName = buildingName || entityBuildingName;
  const finalPostalCode = postalCode || entityPostalCode;

  const line1 = [finalBlockNo, finalStreetName].filter(Boolean).join(' ');
  const line2 = [finalAddrLevel, finalAddrUnit].filter(Boolean).join('-');
  const unit = line2 ? `#${line2}` : '';
  const assembled = [line1, unit, finalBuildingName, finalPostalCode ? `SINGAPORE ${finalPostalCode}` : ''].filter(Boolean).join(', ').trim();
  if (assembled) return assembled;

  return String(
    ent.companyAddress ?? ent.address ?? ent.ADDRESS ?? ent.ADDR ?? ent.addr1 ?? ent.ADDR1 ?? '',
  ).trim();
}
function suggestionKey(field: string, value: string): string {
  return `${field}:${value}`;
}
function isSuggestionDismissed(field: string, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}
function dismissSuggestion(field: string, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}
function shouldShowDateSuggestion(field: string, value: string): boolean {
  if (!value) return false;
  return !isSuggestionDismissed(field, value);
}
function applySuggestedDate(value: string) {
  if (!value) return;
  transactionDate.value = value;
}
function formatIsoForSession(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return iso;
  return formatDateByUserFormat(d);
}

const docBuckets = computed(() => {
  const out = { company: [] as string[], application: [] as string[], directors: [] as string[], members: [] as string[] };
  for (const d of props.documents || []) {
    const name = trim(d.templateName);
    const code = trim(d.docCode).toUpperCase();
    const label = name || code;
    if (!label) continue;
    if (code === 'PRS011' || /share\s*application/i.test(name)) out.application.push(label);
    if (code === 'PRS013' || (/director/i.test(name) && /allotment/i.test(name))) out.directors.push(label);
    if (code === 'PRS012' || (/(member|shareholder)/i.test(name) && /allotment/i.test(name))) out.members.push(label);
  }
  out.company = [...out.application, ...out.directors, ...out.members];
  return {
    company: Array.from(new Set(out.company)),
    application: Array.from(new Set(out.application)),
    directors: Array.from(new Set(out.directors)),
    members: Array.from(new Set(out.members)),
  };
});

const currencyOptionItems = computed(() =>
  currencyOptions.value
    .filter((option) => option.value && option.value !== 'ALL')
    .map((option) => ({
      title: option.modalTitle || option.title || option.value,
      value: option.value,
    })),
);

async function loadCodeOptions() {
  try {
    const [curRes, classRes, certRes, idTypeRes, addrTypeRes] = await Promise.all([
      baseCodeApi.getCodesByMainCode('CM', 'B08', undefined),
      baseCodeApi.getCodesByMainCode('CM', 'A20', undefined),
      baseCodeApi.getCodesByMainCode('CM', 'A19', undefined),
      baseCodeApi.getCodesByMainCode('CM', 'B03', undefined),
      baseCodeApi.getCodesByMainCode('CM', 'B07', undefined),
    ]);
    const curCodes = (curRes as any)?.codes || (curRes as any)?.data?.codes || [];
    const classCodes = (classRes as any)?.codes || (classRes as any)?.data?.codes || [];
    const certCodes = (certRes as any)?.codes || (certRes as any)?.data?.codes || [];
    const idTypeCodes = (idTypeRes as any)?.codes || (idTypeRes as any)?.data?.codes || [];
    const addrTypeCodes = (addrTypeRes as any)?.codes || (addrTypeRes as any)?.data?.codes || [];
    addressTypeOptions.value = (Array.isArray(addrTypeCodes) ? addrTypeCodes : [])
      .map((code: any) => ({
        title: String(
          code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
        ).trim(),
        value: String(code.totalCode || code.TOTAL_CODE || '').trim(),
      }))
      .filter((opt) => {
        if (!opt.title || !opt.value) return false;
        if (opt.value.slice(-3) === '000') return false;
        const lower = opt.title.toLowerCase();
        if (lower.includes('please select') || lower === 'select') return false;
        return true;
      });
    identificationTypeOptions.value = (Array.isArray(idTypeCodes) ? idTypeCodes : [])
      .map((code: any) => ({
        title: String(
          code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
        ).trim(),
        value: String(code.totalCode || code.TOTAL_CODE || '').trim(),
        bigo1: String(code.bigo1 || code.BIGO1 || '').trim(),
      }))
      .filter((opt) => opt.title && opt.value);

    const curUnique = new Map<string, { title: string; value: string; modalTitle?: string; bigo3?: string }>();
    (Array.isArray(curCodes) ? curCodes : []).forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || curUnique.has(total) || total.slice(-3) === '000') return;
      const codeName = String(
        code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      ).trim();
      const bigo3 = String(code.bigo3 || code.BIGO3 || '').trim();
      const listDisplay = bigo3 || codeName || total;
      const modalDisplay = bigo3 && codeName ? `${bigo3} (${codeName})` : listDisplay;
      curUnique.set(total, {
        title: listDisplay,
        value: total,
        modalTitle: modalDisplay,
        bigo3,
      });
    });
    const allCurrencies = Array.from(curUnique.values());
    const jurisdiction = String(sessionStore.getCustomerInfo()?.jurisdiction || '').toUpperCase();
    const jurisdictionCurrencyMap: Record<string, string> = {
      SG: 'SGD', MY: 'MYR', VN: 'VND', CN: 'CNY', HK: 'HKD',
      KR: 'KRW', JP: 'JPY', TH: 'THB', ID: 'IDR', PH: 'PHP',
      IN: 'INR', AU: 'AUD', GB: 'GBP', US: 'USD', TW: 'TWD',
    };
    const targetCurrencyCode = jurisdiction ? jurisdictionCurrencyMap[jurisdiction] : null;
    currencyOptions.value = allCurrencies.sort((a, b) => {
      const aBigo3 = (a.bigo3 || '').toUpperCase();
      const bBigo3 = (b.bigo3 || '').toUpperCase();
      if (targetCurrencyCode) {
        if (aBigo3 === targetCurrencyCode && bBigo3 !== targetCurrencyCode) return -1;
        if (aBigo3 !== targetCurrencyCode && bBigo3 === targetCurrencyCode) return 1;
      }
      if (aBigo3 === 'USD' && bBigo3 !== 'USD') return -1;
      if (aBigo3 !== 'USD' && bBigo3 === 'USD') return 1;
      return aBigo3.localeCompare(bBigo3);
    });

    const classUnique = new Map<string, { title: string; value: string }>();
    (Array.isArray(classCodes) ? classCodes : []).forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const name = String(
        code.codeName || code.CODE_NAME || code.defaultCodeName || code.DEFAULT_CODE_NAME || '',
      ).trim();
      const nameLower = name.toLowerCase();
      if (
        !total ||
        !name ||
        classUnique.has(total) ||
        total.slice(-3) === '000' ||
        nameLower.includes('please select') ||
        nameLower.includes('select')
      ) {
        return;
      }
      classUnique.set(total, { title: name, value: total });
    });
    classOfSharesOptions.value = Array.from(classUnique.values());
    const certUnique = new Map<string, { title: string; value: string }>();
    (Array.isArray(certCodes) ? certCodes : []).forEach((code: any) => {
      const total = String(code.totalCode || code.TOTAL_CODE || '').trim();
      if (!total || certUnique.has(total) || total.slice(-3) === '000') return;
      const codeName = String(
        code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      ).trim();
      if (codeName) certUnique.set(total, { title: codeName, value: total });
    });
    certificateTypeOptions.value = Array.from(certUnique.values());
  } catch (e) {
    console.warn('[AllotmentPresetForm] code options load failed:', e);
  }
}

async function loadEntityMeta(cd: string) {
  try {
    const res = await fetch(`/api/startTask/common/entity/${encodeURIComponent(cd)}`, { credentials: 'include' });
    const json: any = await res.json();
    const e = json?.data?.entity || json?.data || json?.entity || null;
    if (!e) return;
    entityName.value = e.companyName || e.entityName || entityName.value;
    entityRegNo.value = e.registrationNo || e.regNo || entityRegNo.value;
    entityTypeName.value = e.entityTypeName || '';
    companyTypeName.value = e.companyTypeName || '';
    const address = buildAddressFromEntity(e);
    if (address) companyAddress.value = address;
  } catch (e) {
    console.warn('[AllotmentPresetForm] entity meta load failed:', e);
  }
}

async function loadNextCertificateNo(cd: string) {
  try {
    const res: any = await getAllotmentNextCertificateNo(cd);
    const nextNo =
      res?.data?.data?.nextCertificateNo ||
      res?.data?.nextCertificateNo ||
      res?.nextCertificateNo ||
      '';
    certificateNo.value = trim(nextNo);
  } catch (e) {
    console.warn('[AllotmentPresetForm] next certificate no load failed:', e);
  }
}

function addressRowRef(row: ShareholderRow): Record<string, unknown> {
  return row as unknown as Record<string, unknown>;
}

function clearShareholderAddressFields(row: ShareholderRow) {
  row.addressType = SG_ADDR_LOCAL;
  row.postalCode = '';
  row.blockNo = '';
  row.streetName = '';
  row.buildingName = '';
  row.levelNo = '';
  row.unitNo = '';
  row.foreignAddr1 = '';
  row.foreignAddr2 = '';
  row.alternateAddressCd = SG_ADDR_LOCAL;
  row.altPostalCode = '';
  row.altBlockNo = '';
  row.altStreetName = '';
  row.altBuildingName = '';
  row.altAddrLevel = '';
  row.altAddrUnit = '';
  row.altForeignAddr1 = '';
  row.altForeignAddr2 = '';
  row.primaryAddress = '';
  row.addressDisplayLine = '';
}

function syncShareholderAddressDisplay(row: ShareholderRow) {
  refreshShareholderAddressDisplay(addressRowRef(row));
}

function applyContactToShareholder(row: ShareholderRow, c: any) {
  clearShareholderAddressFields(row);
  const contactCd = trim(c?.CONTACT_CD || c?.contactCd || '');
  if (contactCd) row.shareholder = contactCd;
  row.shareholderName = c?.CONTACT_NAME || c?.contactName || row.shareholderName;
  row.categoryCd = c?.CATEGORY_CD || c?.categoryCd || '';
  row.idTypeCd = c?.ID_TYPE_CD || c?.idTypeCd || '';
  row.nationalityCd = c?.NATIONALITY_CD || c?.nationalityCd || '';
  row.countryCd = c?.COUNTRY_CD || c?.countryCd || '';
  row.dateOfBirth = c?.DATE_OF_BIRTH || c?.dateOfBirth || '';
  row.mobileNo = c?.MOBILE_NO || c?.mobileNo || '';
  row.contactEmail = c?.CONTACT_EMAIL || c?.contactEmail || '';
  row.contactRegNo = c?.CONTACT_REG_NO || c?.contactRegNo || '';
  row.addressType = c?.ADDRESS_TYPE_CD || c?.addressTypeCd || SG_ADDR_LOCAL;
  row.postalCode = c?.POSTAL_CODE || c?.postalCode || '';
  row.blockNo = c?.BLOCK_NO || c?.blockNo || '';
  row.streetName = c?.STREET_NAME || c?.streetName || '';
  row.buildingName = c?.BUILDING_NAME || c?.buildingName || '';
  row.levelNo = c?.ADDR_LEVEL || c?.addrLevel || '';
  row.unitNo = c?.ADDR_UNIT || c?.addrUnit || '';
  row.foreignAddr1 = c?.FOREIGN_ADDRESS_LINE_1 || c?.foreignAddressLine1 || '';
  row.foreignAddr2 = c?.FOREIGN_ADDRESS_LINE_2 || c?.foreignAddressLine2 || '';
  row.alternateAddressCd =
    c?.ALTERNATE_ADDRESS_CD || c?.alternateAddressCd || row.addressType || SG_ADDR_LOCAL;
  row.altPostalCode = c?.ALT_POSTAL_CODE || c?.altPostalCode || '';
  row.altBlockNo = c?.ALT_BLOCK_NO || c?.altBlockNo || '';
  row.altStreetName = c?.ALT_STREET_NAME || c?.altStreetName || '';
  row.altBuildingName = c?.ALT_BUILDING_NAME || c?.altBuildingName || '';
  row.altAddrLevel = c?.ALT_ADDR_LEVEL || c?.altAddrLevel || '';
  row.altAddrUnit = c?.ALT_ADDR_UNIT || c?.altAddrUnit || '';
  row.altForeignAddr1 = c?.ALT_FOREIGN_ADDRESS_LINE_1 || c?.altForeignAddressLine1 || '';
  row.altForeignAddr2 = c?.ALT_FOREIGN_ADDRESS_LINE_2 || c?.altForeignAddressLine2 || '';
  row.idTypeName = resolveIdTypeDocumentLabel(row.idTypeCd);
  row.activeAddressSlot = 'residential';
  syncShareholderPrimaryAddressLine(addressRowRef(row));
  syncShareholderAddressDisplay(row);
}

function shareholderAddressLine(sh: ShareholderRow): string {
  const line = trim(sh.addressDisplayLine);
  if (line) return line;
  const slot = normalizeShareholderAddressSlot(sh.activeAddressSlot);
  return formatShareholderAddressLine(addressRowRef(sh), slot);
}

function showShareholderAddressChips(sh: ShareholderRow): boolean {
  const row = addressRowRef(sh);
  return (
    hasShareholderAddressSlotData(row, 'residential')
    || hasShareholderAddressSlotData(row, 'contact')
  );
}

function setShareholderAddressSlot(sh: ShareholderRow, slot: ShareholderAddressSlot) {
  sh.activeAddressSlot = slot;
  syncShareholderAddressDisplay(sh);
}

function openAddressDialog(sh: ShareholderRow, slot?: ShareholderAddressSlot) {
  addressDialogRow.value = sh;
  addressDialogSlot.value = slot || normalizeShareholderAddressSlot(sh.activeAddressSlot);
  addressDialogOpen.value = true;
}

function closeAddressDialog() {
  if (addressDialogRow.value) {
    addressDialogRow.value.activeAddressSlot = addressDialogSlot.value;
    syncShareholderPrimaryAddressLine(addressRowRef(addressDialogRow.value));
    syncShareholderAddressDisplay(addressDialogRow.value);
  }
  addressDialogOpen.value = false;
  addressDialogRow.value = null;
  void nextTick(() => emitValidity());
}

async function hydrateShareholderFromContact(row: ShareholderRow, contactCd: string) {
  const cd = trim(contactCd);
  if (!cd) return;
  row.shareholder = cd;
  try {
    const res: any = await contactsApi.getContactById(cd, { entityCd: entityCd.value || undefined });
    const contact = (res as any)?.contact || (res as any)?.data?.contact || (res as any)?.data || null;
    if (contact && typeof contact === 'object') {
      applyContactToShareholder(row, contact);
      row.shareholder = cd;
    }
  } catch (e) {
    console.warn('[AllotmentPresetForm] contact load failed:', e);
  } finally {
    emitValidity();
  }
}
function requestLookupForShareholder(row: ShareholderRow, rowIndex: number) {
  emit('request-person-lookup', {
    fieldGroup: 'allotteeShareholder',
    defaultRole: 'CONTACT',
    rowId: row.id,
    rowIndex,
  });
}
function addShareholder() {
  shareholderRows.value.push(createShareholderRow());
}
function removeShareholder(idx: number) {
  if (shareholderRows.value.length <= 1) return;
  shareholderRows.value.splice(idx, 1);
}
function addCurrency(row: ShareholderRow) {
  row.currencies.push(createCurrencyRow());
}
function removeCurrency(row: ShareholderRow, idx: number) {
  if (row.currencies.length <= 1) return;
  row.currencies.splice(idx, 1);
}
function addShareClass(cur: CurrencyRow) {
  cur.shareClasses.push(createShareClassRow());
}
function removeShareClass(cur: CurrencyRow, idx: number) {
  if (cur.shareClasses.length <= 1) return;
  cur.shareClasses.splice(idx, 1);
}
function syncCurrencyName(cur: CurrencyRow) {
  cur.currencyName = getLabelByCode(cur.currency, currencyOptions.value);
}
function syncClassName(sc: ShareClassRow) {
  sc.classOfSharesName = getLabelByCode(sc.classOfShares, classOfSharesOptions.value);
}

function clearEntitySharesCache() {
  entitySharesCacheCd = '';
  entitySharesCacheList = [];
}

async function loadEntitySharesCache(cd: string): Promise<Array<Record<string, unknown>>> {
  const key = trim(cd);
  if (!key) return [];
  if (entitySharesCacheCd === key) return entitySharesCacheList;
  entitySharesCacheList = await fetchEntitySharesList(key);
  entitySharesCacheCd = key;
  return entitySharesCacheList;
}

async function refreshShareClassBaseline(
  sc: ShareClassRow,
  cur: CurrencyRow,
  resetManual = false,
) {
  if (resetManual) sc.issuedManuallyEdited = false;
  const cd = trim(entityCd.value);
  const currency = trim(cur.currency);
  const classCd = trim(sc.classOfShares);
  if (!cd || !currency || !classCd) {
    sc.entityIssuedBaseline = '';
    if (!sc.issuedManuallyEdited) {
      sc.issuedShareCapital = computeIssuedFromBaseline(0, sc.paidUpShareCapital);
    }
    return;
  }
  try {
    const shares = await loadEntitySharesCache(cd);
    const baseline = sumEntityIssuedBaseline(shares, currency, classCd, sc.classOfSharesName);
    sc.entityIssuedBaseline = formatShareCapitalAmount(baseline);
    if (!sc.issuedManuallyEdited) {
      sc.issuedShareCapital = computeIssuedFromBaseline(baseline, sc.paidUpShareCapital);
    }
  } catch (e) {
    console.warn('[AllotmentPresetForm] entity shares baseline load failed:', e);
  }
}

async function refreshAllShareClassBaselines(resetManual = false) {
  for (const sh of shareholderRows.value) {
    for (const cur of sh.currencies) {
      for (const sc of cur.shareClasses) {
        await refreshShareClassBaseline(sc, cur, resetManual);
      }
    }
  }
}

async function onCurrencyChange(cur: CurrencyRow) {
  syncCurrencyName(cur);
  for (const sc of cur.shareClasses) {
    await refreshShareClassBaseline(sc, cur, true);
  }
}

async function onClassOfSharesChange(sc: ShareClassRow, cur: CurrencyRow) {
  syncClassName(sc);
  await refreshShareClassBaseline(sc, cur, true);
}

function onPaidUpShareCapitalInput(sc: ShareClassRow, value: string) {
  sc.paidUpShareCapital = value;
  if (!sc.issuedManuallyEdited) {
    const baseline = parseShareCapitalAmount(sc.entityIssuedBaseline);
    sc.issuedShareCapital = computeIssuedFromBaseline(baseline, value);
  }
}

function onIssuedShareCapitalInput(sc: ShareClassRow, value: string) {
  sc.issuedShareCapital = value;
  sc.issuedManuallyEdited = true;
}
function getClassOfSharesAs(classOfSharesCd: string): string {
  const cd = trim(classOfSharesCd);
  if (!cd) return '';
  if (cd === 'A20001') return 'ORD';
  if (cd === 'A20002') return 'PREF';
  if (cd === 'A20003') return 'OTH';
  const n = getLabelByCode(cd, classOfSharesOptions.value).toLowerCase();
  if (n.includes('ordinary')) return 'ORD';
  if (n.includes('preference') || n.includes('pref')) return 'PREF';
  if (n.includes('other')) return 'OTH';
  return '';
}
function getCurrencyAs(currencyCd: string): string {
  const cd = trim(currencyCd).toUpperCase();
  if (!cd) return '';
  return cd;
}
function getSharesPayableText(sharesPayable: string): string {
  const value = trim(sharesPayable);
  const textMap: Record<string, string> = {
    'all-in-cash': 'In Cash',
    'all-otherwise': 'Otherwise than in Cash',
    'no-consideration': 'No Consideration',
    partially: 'Partially in Cash and Otherwise than in Cash',
  };
  return textMap[value] || value;
}
function getCountryBigo3WithoutPlus(countryCd: string): string {
  return trim(countryCd).replace(/^\+/, '');
}

function hasRequiredError(key: string): boolean {
  return requiredErrorKeys.value.has(key);
}

function setRequiredErrors(keys: string[]) {
  requiredErrorKeys.value = new Set(keys);
}

function firstMissingRequiredKeys(): string[] {
  const missing: string[] = [];
  const pushIfEmpty = (key: string, value: unknown) => {
    if (!String(value ?? '').trim()) missing.push(key);
  };

  pushIfEmpty('transactionDate', transactionDate.value);
  if (!shareholderRows.value.length) {
    missing.push('shareholders.min');
    return missing;
  }
  shareholderRows.value.forEach((sh, sIdx) => {
    if (!trim(sh.shareholder) || !trim(sh.shareholderName)) {
      missing.push(`shareholders.${sIdx}.shareholder`);
    }
    if (!shareholderAddressLine(sh).trim()) {
      missing.push(`shareholders.${sIdx}.address`);
    }
    sh.currencies.forEach((cur, cIdx) => {
      pushIfEmpty(`shareholders.${sIdx}.currencies.${cIdx}.currency`, cur.currency);
      pushIfEmpty(`shareholders.${sIdx}.currencies.${cIdx}.sharesPayable`, cur.sharesPayable);
      cur.shareClasses.forEach((sc, scIdx) => {
        pushIfEmpty(
          `shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.classOfShares`,
          sc.classOfShares,
        );
        pushIfEmpty(
          `shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.numberOfShares`,
          sc.numberOfShares,
        );
      });
    });
  });
  return missing;
}

async function focusRequiredField(key: string): Promise<void> {
  await nextTick();
  const root = formRoot.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(`[data-required-key="${key}"]`);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = target.querySelector<HTMLElement>('input, textarea, [role="combobox"], button');
  focusable?.focus();
}

async function validateAndFocusRequiredFields(): Promise<boolean> {
  if (!trim(entityCd.value)) return false;
  const missingKeys = firstMissingRequiredKeys();
  setRequiredErrors(missingKeys);
  if (missingKeys.length === 0) return true;
  await focusRequiredField(missingKeys[0]);
  return false;
}

function validateAllShareholders(): boolean {
  if (!shareholderRows.value.length) return false;
  for (const sh of shareholderRows.value) {
    if (!trim(sh.shareholder) || !trim(sh.shareholderName)) return false;
    if (!shareholderAddressLine(sh).trim()) return false;
    for (const cur of sh.currencies) {
      if (!trim(cur.currency) || !trim(cur.sharesPayable)) return false;
      for (const sc of cur.shareClasses) {
        if (!trim(sc.classOfShares) || !trim(sc.numberOfShares)) return false;
      }
    }
  }
  return true;
}
function validateAll(): boolean {
  if (!trim(entityCd.value)) return false;
  return firstMissingRequiredKeys().length === 0;
}

function emitValidity() {
  emit('validity-change', validateAll());
}

function toLegacyShareholders(): any[] {
  return shareholderRows.value.map((sh) => {
    syncShareholderPrimaryAddressLine(addressRowRef(sh));
    return {
    shareholder: trim(sh.shareholder),
    shareholderName: trim(sh.shareholderName),
    categoryCd: trim(sh.categoryCd),
    idTypeCd: trim(sh.idTypeCd),
    idTypeName: resolveIdTypeDocumentLabel(sh.idTypeCd) || trim(sh.idTypeName),
    contactRegNo: trim(sh.contactRegNo),
    contactEmail: trim(sh.contactEmail),
    nationalityCd: trim(sh.nationalityCd),
    countryCd: trim(sh.countryCd),
    dateOfBirth: trim(sh.dateOfBirth),
    mobileNo: trim(sh.mobileNo),
    representativeName: trim(sh.representativeName),
    representativeCd: trim(sh.representativeCd),
    ...serializeShareholderAddressFields(addressRowRef(sh)),
    currencies: sh.currencies.map((cur) => ({
      currency: trim(cur.currency),
      currencyName: trim(cur.currencyName) || getLabelByCode(cur.currency, currencyOptions.value),
      sharesPayable: trim(cur.sharesPayable),
      shareClasses: cur.shareClasses.map((sc) => ({
        classOfShares: trim(sc.classOfShares),
        classOfSharesName: trim(sc.classOfSharesName) || getLabelByCode(sc.classOfShares, classOfSharesOptions.value),
        numberOfShares: trim(sc.numberOfShares),
        entityIssuedBaseline: trim(sc.entityIssuedBaseline),
        issuedShareCapital: trim(sc.issuedShareCapital),
        paidUpShareCapital: trim(sc.paidUpShareCapital),
        issuedManuallyEdited: !!sc.issuedManuallyEdited,
        subClasses: sc.subClasses || [],
        treasuryShares: sc.treasuryShares || [],
      })),
    })),
  };
  });
}

function getFormData(): Record<string, any> {
  const shareholders = toLegacyShareholders();
  const firstSh = shareholders[0] || {};
  const firstCur = firstSh.currencies?.[0] || {};
  const firstCls = firstCur.shareClasses?.[0] || {};
  return {
    entity: trim(entityCd.value),
    entityCd: trim(entityCd.value),
    entityName: trim(entityName.value),
    entityRegNo: trim(entityRegNo.value),
    companyAddress: trim(companyAddress.value),
    transactionDate: trim(transactionDate.value),
    priorApprovalFiled: trim(priorApprovalFiled.value),
    egmPlace: '',
    egmTime: '10:00',
    egmDate: trim(transactionDate.value),
    shareholders,
    shareholder: trim(firstSh.shareholder),
    shareholderName: trim(firstSh.shareholderName),
    currency: trim(firstCur.currency),
    currencyName: trim(firstCur.currencyName),
    classOfShares: trim(firstCls.classOfShares),
    classOfSharesName: trim(firstCls.classOfSharesName),
    numberOfShares: trim(firstCls.numberOfShares),
    issuedShareCapital: trim(firstCls.issuedShareCapital),
    paidUpShareCapital: trim(firstCls.paidUpShareCapital),
    sharesPayable: trim(firstCur.sharesPayable),
    certificateType: trim(certificateType.value),
    certificateNo: trim(certificateNo.value),
    chairPerson: trim(chairPerson.value),
  };
}

function transformCodeNameToValue(payload: any): any {
  if (Array.isArray(payload)) return payload.map(transformCodeNameToValue);
  if (!payload || typeof payload !== 'object') return payload;
  if ('code' in payload && 'name' in payload && Object.keys(payload).length <= 3) {
    return payload.name || payload.code || '';
  }
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(payload)) out[k] = transformCodeNameToValue(v);
  return out;
}

async function getEFilingData() {
  const filingAgentUEN = sessionStore.getCustomerInfo()?.regNo || '';
  const list: any[] = [];
  for (const sh of shareholderRows.value) {
    syncShareholderPrimaryAddressLine(addressRowRef(sh));
    for (const cur of sh.currencies) {
      for (const sc of cur.shareClasses) {
        list.push({
          contactCd: trim(sh.shareholder),
          contactName: trim(sh.shareholderName),
          contactEmail: trim(sh.contactEmail),
          shareholderName: trim(sh.shareholderName),
          uen: trim(sh.contactRegNo),
          transactionType: 'A18001',
          transactionName: 'Allotment',
          transactionDate: trim(transactionDate.value),
          category: { code: trim(sh.categoryCd), name: trim(sh.categoryCd) },
          categoryType: trim(sh.categoryCd) === 'B09001' ? 'CORP' : 'IND',
          categoryCd: trim(sh.categoryCd),
          idType: {
            code: trim(sh.idTypeCd),
            name: resolveIdTypeEfilingLabel(sh.idTypeCd) || trim(sh.idTypeName),
          },
          nationality: { code: trim(sh.nationalityCd), name: trim(sh.nationalityCd || sh.nationalityName) },
          birthDate: trim(sh.dateOfBirth).split(' ')[0] || '',
          hasBirthDate: !!trim(sh.dateOfBirth),
          currency: { code: trim(cur.currency), name: trim(cur.currencyName) },
          currencyCd: trim(cur.currency),
          currencyAs: getCurrencyAs(cur.currency),
          classOfShares: { code: trim(sc.classOfShares), name: trim(sc.classOfSharesName) },
          classOfSharesAs: getClassOfSharesAs(sc.classOfShares),
          numberOfShares: trim(sc.numberOfShares),
          issuedShareCapital: trim(sc.issuedShareCapital),
          paidUpShareCapital: trim(sc.paidUpShareCapital),
          considerationPaid: '0',
          sharesPayable: getSharesPayableText(cur.sharesPayable),
          certiNo: trim(certificateNo.value),
          certiType: trim(certificateType.value),
          chairpersonCd: trim(chairPerson.value),
          mobileNoCountryCode: getCountryBigo3WithoutPlus(sh.countryCd),
          countryCode: getCountryBigo3WithoutPlus(sh.countryCd),
          mobileNo: trim(sh.mobileNo),
          addressType: isSgForeignAddressType(sh.addressType) ? 'F' : 'S',
          postalCode: trim(sh.postalCode),
          blockNo: trim(sh.blockNo),
          blkhseNo: trim(sh.blockNo),
          streetName: trim(sh.streetName),
          buildingName: trim(sh.buildingName),
          levelNo: trim(sh.levelNo),
          unitNo: trim(sh.unitNo),
          foreignAddr1: trim(sh.foreignAddr1),
          foreignAddr2: trim(sh.foreignAddr2),
          contactAddressType: isSgForeignAddressType(sh.alternateAddressCd || sh.addressType) ? 'F' : 'S',
          contactPostalCode: trim(sh.altPostalCode),
          contactUnitNo: trim(sh.altAddrUnit),
          contactLevelNo: trim(sh.altAddrLevel),
          contactBlkhseNo: trim(sh.altBlockNo),
          contactStreetName: trim(sh.altStreetName),
          contactBuildingName: trim(sh.altBuildingName),
          contactForeignAddr1: trim(sh.altForeignAddr1),
          contactForeignAddr2: trim(sh.altForeignAddr2),
          representativeCd: trim(sh.representativeCd),
          representativeName: trim(sh.representativeName),
          entityUen: trim(entityRegNo.value),
          transferToIdx: null,
          transferToName: '',
          tranListIdx: 0,
          tranDetailIdx: 0,
        });
      }
    }
  }

  // 통화 정규화: 같은 통화는 1행으로 병합(주식수/납입금 합산), issuedShareCapital은 통화별 회사 총계로 통일.
  // 통화가 다르면 행을 분리 유지. (SG Allotment 프리셋 전용)
  const entityShares = await loadEntitySharesCache(trim(entityCd.value));
  const normalizedList = mergeAllotmentShareholderListByCurrency(list, entityShares);

  const eFilingDataValidation = {
    ShareholderList: normalizedList,
    EntityUEN: trim(entityRegNo.value),
    FilingAgentUEN: trim(filingAgentUEN),
    entityName: trim(entityName.value),
  };
  const eFilingDataForApi = transformCodeNameToValue(eFilingDataValidation);
  return { eFilingDataValidation, eFilingDataForApi };
}

function setFormData(data: any) {
  if (!data || typeof data !== 'object') return;
  entityCd.value = trim(data.entityCd || data.entity || entityCd.value);
  const nextEntityName = trim(data.entityName?.value || data.entityName || data.companyName || '');
  const nextEntityRegNo = trim(data.entityRegNo?.value || data.entityRegNo || data.companyRegistrationNo || '');
  if (nextEntityName) entityName.value = nextEntityName;
  if (nextEntityRegNo) entityRegNo.value = nextEntityRegNo;
  const nextCompanyAddress = trim(
    data.companyAddress?.value ?? data.companyAddress ?? data.company_address ?? '',
  );
  if (nextCompanyAddress) {
    companyAddress.value = nextCompanyAddress;
  } else if (!trim(companyAddress.value) && trim(entityCd.value)) {
    void loadEntityMeta(trim(entityCd.value));
  }
  transactionDate.value = trim(data.transactionDate || transactionDate.value);
  priorApprovalFiled.value = trim(data.priorApprovalFiled || '');
  certificateType.value = trim(data.certificateType || '');
  certificateNo.value = trim(data.certificateNo || '');
  chairPerson.value = trim(data.chairPerson || '');

  const rows = Array.isArray(data.shareholders) ? data.shareholders : [];
  if (rows.length > 0) {
    shareholderRows.value = rows.map((sh: any) => {
      const row = createShareholderRow();
      row.shareholder = trim(sh.shareholder);
      row.shareholderName = trim(sh.shareholderName);
      row.categoryCd = trim(sh.categoryCd);
      row.idTypeCd = trim(sh.idTypeCd);
      row.idTypeName = resolveIdTypeDocumentLabel(row.idTypeCd) || trim(sh.idTypeName);
      row.contactRegNo = trim(sh.contactRegNo);
      row.contactEmail = trim(sh.contactEmail);
      row.representativeName = trim(sh.representativeName);
      row.representativeCd = trim(sh.representativeCd);
      row.countryCd = trim(sh.countryCd);
      row.nationalityCd = trim(sh.nationalityCd);
      row.dateOfBirth = trim(sh.dateOfBirth);
      row.mobileNo = trim(sh.mobileNo);
      applySerializedShareholderAddressFields(addressRowRef(row), sh);
      row.currencies = (Array.isArray(sh.currencies) ? sh.currencies : []).map((cur: any) => ({
        id: uid('cur'),
        currency: trim(cur.currency),
        currencyName: trim(cur.currencyName),
        sharesPayable: trim(cur.sharesPayable),
        shareClasses: (Array.isArray(cur.shareClasses) ? cur.shareClasses : []).map((sc: any) => ({
          id: uid('cls'),
          classOfShares: trim(sc.classOfShares),
          classOfSharesName: trim(sc.classOfSharesName),
          numberOfShares: trim(sc.numberOfShares),
          entityIssuedBaseline: trim(sc.entityIssuedBaseline),
          issuedShareCapital: trim(sc.issuedShareCapital),
          paidUpShareCapital: trim(sc.paidUpShareCapital),
          issuedManuallyEdited: sc.issuedManuallyEdited === true || trim(sc.issuedManuallyEdited) === 'true',
          subClasses: Array.isArray(sc.subClasses) ? sc.subClasses : [],
          treasuryShares: Array.isArray(sc.treasuryShares) ? sc.treasuryShares : [],
        })),
      }));
      if (!row.currencies.length) row.currencies = [createCurrencyRow()];
      syncShareholderAddressDisplay(row);
      return row;
    });
  } else {
    shareholderRows.value = [createShareholderRow()];
  }
  void refreshAllShareClassBaselines(false);
}

watch(
  () => props.entityId,
  async (newEntityId) => {
    const cd = trim(newEntityId);
    if (!cd) return;
    entityCd.value = cd;
    clearEntitySharesCache();
    await Promise.all([
      loadEntityMeta(cd),
      loadNextCertificateNo(cd),
      loadCodeOptions(),
    ]);
    await refreshAllShareClassBaselines(true);
  },
  { immediate: true },
);

watch(
  () => props.initialData,
  (data) => {
    if (data && typeof data === 'object') setFormData(data.formData || data);
  },
  { immediate: true, deep: true },
);

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx) return;
    if (ctx.fieldGroup !== 'allotteeShareholder') return;
    const rowId = trim((ctx as any).rowId);
    const rowIndex = Number((ctx as any).rowIndex);
    let targetRow =
      shareholderRows.value.find((r) => r.id === rowId) ||
      (Number.isInteger(rowIndex) && rowIndex >= 0 ? shareholderRows.value[rowIndex] : null) ||
      shareholderRows.value[0];
    if (!targetRow) return;

    const raw = person as any;
    const contactCd = trim(raw.contactCd || raw.CONTACT_CD || '');
    const contactName = trim(raw.name || raw.contactName || raw.CONTACT_NAME || '');
    if (contactCd) {
      void hydrateShareholderFromContact(targetRow, contactCd);
      return;
    }
    if (contactName) targetRow.shareholderName = contactName;
    applyContactToShareholder(targetRow, raw);
    emitValidity();
  },
);

watch(
  [shareholderRows, transactionDate, entityCd],
  () => {
    emitValidity();
    if (requiredErrorKeys.value.size === 0) return;
    setRequiredErrors(firstMissingRequiredKeys());
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  if (!shareholderRows.value.length) shareholderRows.value = [createShareholderRow()];
  await loadCodeOptions();
  emitValidity();
});

defineExpose({
  setFormData,
  getFormData,
  getEFilingData,
  getIdTypeOptionsForDocuments: idTypeOptionsSnapshot,
  validateAllShareholders,
  validateAll,
  validateAndFocusRequiredFields,
});
</script>

<template>
  <div ref="formRoot" class="asf-wrap">
    <div class="asf-section">
      <div class="asf-header">
        <Icon icon="mdi:domain" width="16" color="#666" />
        <span>Company</span>
        <div class="asf-docs">
          <span class="asf-docs-label">Applies to:</span>
          <v-chip v-for="(n, i) in docBuckets.company.slice(0, MAX_DOC_CHIPS)" :key="'co-' + i" size="x-small" color="primary" variant="tonal">{{ n }}</v-chip>
        </div>
      </div>
      <v-row dense class="pa-3">
        <v-col cols="12" md="6">
          <v-text-field v-model="entityName" label="Company name" density="compact" variant="outlined" :readonly="true" />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="entityRegNo" label="UEN / Reg. No." density="compact" variant="outlined" :readonly="true" />
        </v-col>
      </v-row>
    </div>

    <div class="asf-section">
      <div class="asf-header">
        <Icon icon="mdi:file-document-edit-outline" width="16" color="#666" />
        <span>Share Application</span>
        <div class="asf-docs">
          <span class="asf-docs-label">Applies to:</span>
          <v-chip v-for="(n, i) in docBuckets.application.slice(0, MAX_DOC_CHIPS)" :key="'app-' + i" size="x-small" color="primary" variant="tonal">{{ n }}</v-chip>
        </div>
      </div>
      <v-row dense class="pa-3">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="transactionDateLocalText"
            :placeholder="transactionDatePlaceholder"
            label="Application date *"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :disabled="!!readOnly"
            :error="hasRequiredError('transactionDate')"
            data-required-key="transactionDate"
            class="asf-required-field"
            @blur="transactionDateOnBlur(($event.target as HTMLInputElement).value)"
          >
            <template #append-inner>
              <v-menu
                v-model="transactionDateMenu"
                :close-on-content-click="false"
                location="bottom"
                :disabled="!!readOnly"
              >
                <template #activator="{ props: menuProps }">
                  <v-icon
                    v-bind="menuProps"
                    color="primary"
                    size="20"
                    style="cursor: pointer"
                  >
                    mdi-calendar
                  </v-icon>
                </template>
                <v-date-picker
                  v-bind="transactionDatePickerProps"
                  v-model="transactionDatePickerValue"
                  @update:model-value="transactionDateMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
          <v-chip
            v-if="shouldShowDateSuggestion('transactionDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 asf-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate(suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('transactionDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="certificateType"
            :items="certificateTypeOptions"
            item-title="title"
            item-value="value"
            label="Certificate Type"
            density="compact"
            variant="outlined"
            :readonly="!!readOnly"
            clearable
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field v-model="certificateNo" label="Certificate No" density="compact" variant="outlined" :readonly="!!readOnly" />
        </v-col>
      </v-row>
    </div>

    <div class="asf-section">
      <div class="asf-header">
        <Icon icon="mdi:account-group-outline" width="16" color="#666" />
        <span>Allottees</span>
        <v-spacer />
        <v-btn size="small" variant="tonal" color="primary" :disabled="!!readOnly" @click="addShareholder">Add Shareholder</v-btn>
      </div>
      <div class="pa-3 asf-stack">
        <div v-for="(sh, sIdx) in shareholderRows" :key="sh.id" class="asf-card">
          <div class="asf-card-top">
            <strong>Shareholder {{ sIdx + 1 }}</strong>
            <v-btn size="x-small" variant="text" color="error" :disabled="!!readOnly || shareholderRows.length <= 1" @click="removeShareholder(sIdx)">Remove</v-btn>
          </div>
          <v-row dense class="asf-loop-row">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="sh.shareholderName"
                label="Shareholder *"
                density="compact"
                variant="outlined"
                hide-details="auto"
                :readonly="!!readOnly"
                :error="hasRequiredError(`shareholders.${sIdx}.shareholder`)"
                :data-required-key="`shareholders.${sIdx}.shareholder`"
                class="asf-required-field"
              >
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        :disabled="!!readOnly"
                        @click.stop="requestLookupForShareholder(sh, sIdx)"
                      />
                    </template>
                    <span>Lookup contact from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="7">
              <v-text-field
                :model-value="sh.addressDisplayLine"
                label="Address *"
                density="compact"
                variant="outlined"
                hide-details="auto"
                readonly
                :error="hasRequiredError(`shareholders.${sIdx}.address`)"
                :data-required-key="`shareholders.${sIdx}.address`"
                class="asf-required-field"
              >
                <template v-if="!readOnly" #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-pencil-outline"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click.stop="openAddressDialog(sh)"
                      />
                    </template>
                    <span>Edit address</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="showShareholderAddressChips(sh)" class="asf-addr-suggest">
                <v-chip
                  v-if="hasShareholderAddressSlotData(addressRowRef(sh), 'residential')"
                  size="x-small"
                  variant="tonal"
                  :color="sh.activeAddressSlot === 'residential' ? 'primary' : 'default'"
                  @click="setShareholderAddressSlot(sh, 'residential')"
                >
                  Residential Address
                </v-chip>
                <v-chip
                  v-if="hasShareholderAddressSlotData(addressRowRef(sh), 'contact')"
                  size="x-small"
                  variant="tonal"
                  :color="sh.activeAddressSlot === 'contact' ? 'primary' : 'default'"
                  @click="setShareholderAddressSlot(sh, 'contact')"
                >
                  Contact Address
                </v-chip>
              </div>
            </v-col>
          </v-row>

          <div v-for="(cur, cIdx) in sh.currencies" :key="cur.id" class="asf-card-sub">
            <div class="asf-card-top">
              <span>Currency {{ cIdx + 1 }}</span>
              <div class="d-flex ga-2">
                <v-btn size="x-small" variant="text" color="primary" :disabled="!!readOnly" @click="addShareClass(cur)">Add Class</v-btn>
                <v-btn size="x-small" variant="text" color="error" :disabled="!!readOnly || sh.currencies.length <= 1" @click="removeCurrency(sh, cIdx)">Remove Currency</v-btn>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="cur.currency"
                  :items="currencyOptionItems"
                  item-title="title"
                  item-value="value"
                  label="Currency *"
                  placeholder="Please Select"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :readonly="!!readOnly"
                  :error="hasRequiredError(`shareholders.${sIdx}.currencies.${cIdx}.currency`)"
                  :data-required-key="`shareholders.${sIdx}.currencies.${cIdx}.currency`"
                  class="asf-required-field"
                  clearable
                  @update:model-value="onCurrencyChange(cur)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="cur.sharesPayable"
                  :items="sharePayableOptions"
                  item-title="title"
                  item-value="value"
                  label="Amount payable *"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :readonly="!!readOnly"
                  :error="hasRequiredError(`shareholders.${sIdx}.currencies.${cIdx}.sharesPayable`)"
                  :data-required-key="`shareholders.${sIdx}.currencies.${cIdx}.sharesPayable`"
                  class="asf-required-field"
                />
              </v-col>
            </v-row>

            <div v-for="(sc, scIdx) in cur.shareClasses" :key="sc.id" class="asf-card-sub2">
              <div class="asf-card-top">
                <span>
                  {{
                    cur.shareClasses.length > 1
                      ? `Share capital ${scIdx + 1}`
                      : 'Share capital'
                  }}
                </span>
                <v-btn size="x-small" variant="text" color="error" :disabled="!!readOnly || cur.shareClasses.length <= 1" @click="removeShareClass(cur, scIdx)">Remove</v-btn>
              </div>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="sc.classOfShares"
                    :items="classOfSharesOptions"
                    item-title="title"
                    item-value="value"
                    label="Class of shares *"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :readonly="!!readOnly"
                    :error="hasRequiredError(`shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.classOfShares`)"
                    :data-required-key="`shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.classOfShares`"
                    class="asf-required-field"
                    @update:model-value="onClassOfSharesChange(sc, cur)"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="sc.numberOfShares"
                    label="Number of shares *"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :readonly="!!readOnly"
                    :error="hasRequiredError(`shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.numberOfShares`)"
                    :data-required-key="`shareholders.${sIdx}.currencies.${cIdx}.classes.${scIdx}.numberOfShares`"
                    class="asf-required-field"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :model-value="sc.issuedShareCapital"
                    label="Issued share capital"
                    density="compact"
                    variant="outlined"
                    :hide-details="!sc.entityIssuedBaseline"
                    :readonly="!!readOnly"
                    :hint="sc.entityIssuedBaseline ? `Entity issued: ${sc.entityIssuedBaseline}` : undefined"
                    persistent-hint
                    @update:model-value="onIssuedShareCapitalInput(sc, $event)"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :model-value="sc.paidUpShareCapital"
                    label="Paid-up share capital"
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    :readonly="!!readOnly"
                    @update:model-value="onPaidUpShareCapitalInput(sc, $event)"
                  />
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="mt-2">
            <v-btn size="small" variant="text" color="primary" :disabled="!!readOnly" @click="addCurrency(sh)">Add Currency</v-btn>
          </div>
        </div>
      </div>
    </div>

    <div class="asf-section">
      <div class="asf-header">
        <Icon icon="mdi:account-tie-hat-outline" width="16" color="#666" />
        <span>Directors' Resolution</span>
        <div class="asf-docs">
          <span class="asf-docs-label">Applies to:</span>
          <v-chip v-for="(n, i) in docBuckets.directors.slice(0, MAX_DOC_CHIPS)" :key="'dr-' + i" size="x-small" color="primary" variant="tonal">{{ n }}</v-chip>
        </div>
      </div>
      <div class="pa-3 text-caption">
        shareholder_approval_date uses <strong>transactionDate</strong>, and signers are all directors.
      </div>
    </div>

    <div class="asf-section">
      <div class="asf-header">
        <Icon icon="mdi:account-multiple-outline" width="16" color="#666" />
        <span>Members' Resolution</span>
        <div class="asf-docs">
          <span class="asf-docs-label">Applies to:</span>
          <v-chip v-for="(n, i) in docBuckets.members.slice(0, MAX_DOC_CHIPS)" :key="'mr-' + i" size="x-small" color="primary" variant="tonal">{{ n }}</v-chip>
        </div>
      </div>
      <div class="pa-3 text-caption">
        Single document, signers are all shareholders/members.
      </div>
    </div>

    <v-dialog v-model="addressDialogOpen" max-width="640" scrollable @click:outside="closeAddressDialog">
      <v-card v-if="addressDialogRow">
        <v-card-title class="text-subtitle-1 py-3 d-flex align-center">
          Edit address
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="closeAddressDialog" />
        </v-card-title>
        <v-card-text class="pt-0">
          <div class="asf-addr-dialog-chips mb-3">
            <v-chip
              size="small"
              variant="tonal"
              :color="addressDialogSlot === 'residential' ? 'primary' : 'default'"
              @click="addressDialogSlot = 'residential'"
            >
              Residential Address
            </v-chip>
            <v-chip
              size="small"
              variant="tonal"
              :color="addressDialogSlot === 'contact' ? 'primary' : 'default'"
              @click="addressDialogSlot = 'contact'"
            >
              Contact Address
            </v-chip>
          </div>
          <SgShareholderAddressBlock
            :row="addressDialogRow"
            :variant="addressDialogSlot"
            :read-only="readOnly"
            :address-type-items="addressTypeOptions"
            compact
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="tonal" color="primary" @click="closeAddressDialog">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.asf-wrap { display: flex; flex-direction: column; gap: 12px; }
.asf-section { border: 1px solid #e5e9f2; border-radius: 12px; overflow: hidden; background: #fff; }
.asf-header {
  display: flex; align-items: center; gap: 6px; padding: 10px 12px;
  font-size: 12px; font-weight: 600; color: #2f3d8a;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}
.asf-docs { margin-left: auto; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.asf-docs-label { font-size: 11px; color: #5d6982; }
.asf-stack { display: flex; flex-direction: column; gap: 10px; }
.asf-card { border: 1px solid #e8edf4; border-radius: 10px; padding: 10px; }
.asf-card-sub { margin-top: 8px; border: 1px dashed #dce3ee; border-radius: 8px; padding: 8px; }
.asf-card-sub2 { margin-top: 8px; border: 1px dashed #e5e9f2; border-radius: 8px; padding: 8px; }
.asf-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; font-size: 12px; }
.asf-loop-row { margin-bottom: 4px; }
.asf-addr-suggest {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.asf-addr-dialog-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.asf-required-helper {
  margin-top: 6px;
  font-size: 12px;
  color: #b3261e;
}
:deep(.asf-required-field.v-input--error .v-label) {
  color: #b3261e !important;
}
</style>

